import { applyProperties } from '../util/helpers';

/**
 *
 * @module HLSJSPlayerConfig
 * @desc The hls.js config object. Passed to the hls.js constructor. This config object is set on `MediaPlayerConfig.playerConfig.hlsjsPlayerConfig`
 * Reference: https://github.com/video-dev/hls.js/blob/master/docs/API.md#fine-tuning
 * @class HLSJSPlayerConfig
 *
 */
class HLSJSPlayerConfig {

    /**
     *
     * @param {object} hlsConfig overrides for the hls.js config
     * @constructor
     *
     */
    constructor(hlsConfig) {

        /**
         *
         * @type {boolean}
         *
         */
        this.autoStartLoad = true;

        /**
         *
         * @type {int}
         *
         */
        this.startPosition  = -1;

        /**
         *
         * @type {boolean}
         *
         */
        this.capLevelToPlayerSize = false;

        /**
         *
         * @type {boolean}
         *
         */
        this.debug = false;

        /**
         *
         * @type {string}
         *
         */
        this.defaultAudioCodec = undefined;

        /**
         *
         * @type {int}
         *
         */
        this.initialLiveManifestSize = 1;

        /**
         *
         * @type {int}
         *
         */
        this.maxBufferLength = 30;

        /**
         *
         * @type {int}
         *
         */
        this.maxMaxBufferLength = 600;

        /**
         *
         * @type {int}
         *
         */
        this.maxBufferSize = 60*1000*1000;

        /**
         *
         * @type {number}
         *
         */
        this.maxBufferHole = 0.5;

        /**
         *
         * @type {number}
         *
         */
        this.lowBufferWatchdogPeriod = 0.5;

        /**
         *
         * @type {int}
         *
         */
        this.highBufferWatchdogPeriod = 3;

        /**
         *
         * @type {number}
         *
         */
        this.nudgeOffset = 0.1;

        /**
         *
         * @type {number}
         *
         */
        this.nudgeMaxRetry  = 3;

        /**
         *
         * @type {number}
         *
         */
        this.maxFragLookUpTolerance = 0.2;

        /**
         *
         * @type {int}
         *
         */
        this.liveSyncDurationCount = 3;

        /**
         *
         * @type {int}
         *
         */
        this.liveMaxLatencyDurationCount = Infinity;

        /**
         *
         * @type {int}
         *
         */
        this.liveSyncDuration = undefined;

        /**
         *
         * @type {int}
         *
         */
        this.liveMaxLatencyDuration = undefined;

        /**
         *
         * @type {bool}
         *
         */
        this.liveDurationInfinity = false;

        /**
         *
         * @type {int}
         *
         */
        this.liveBackBufferLength = Infinity;

        /**
         *
         * @type {boolean}
         *
         */
        this.enableWorker = true;

        /**
         *
         * @type {boolean}
         *
         */
        this.enableSoftwareAES = true;

        /**
         *
         * @type {int}
         *
         */
        this.manifestLoadingTimeOut = 10000;

        /**
         *
         * @type {int}
         *
         */
        this.manifestLoadingMaxRetry = 1;

        /**
         *
         * @type {int}
         *
         */
        this.manifestLoadingRetryDelay = 500;

        /**
         *
         * @type {int}
         *
         */
        this.manifestLoadingMaxRetryTimeout  = 64000;

        /**
         *
         * @type {int}
         *
         */
        this.startLevel = undefined;

        /**
         *
         * @type {int}
         *
         */
        this.levelLoadingTimeOut = 10000;

        /**
         *
         * @type {int}
         *
         */
        this.levelLoadingMaxRetry = 4;

        /**
         *
         * @type {int}
         *
         */
        this.levelLoadingRetryDelay = 500;

        /**
         *
         * @type {int}
         *
         */
        this.levelLoadingMaxRetryTimeout = 64000;

        /**
         *
         * @type {int}
         *
         */
        this.fragLoadingTimeOut = 20000;

        /**
         *
         * @type {int}
         *
         */
        this.fragLoadingMaxRetry = 6;

        /**
         *
         * @type {int}
         *
         */
        this.fragLoadingRetryDelay = 500;

        /**
         *
         * @type {int}
         *
         */
        this.fragLoadingMaxRetryTimeout = 64000;

        /**
         *
         * @type {boolean}
         *
         */
        this.startFragPrefetch = false;

        /**
         *
         * @type {number}
         *
         */
        this.appendErrorMaxRetry = 3;

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires loader to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('loader')) {

            this.loader = hlsConfig.loader;

        }

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires fLoader to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('fLoader')) {

            this.fLoader = hlsConfig.fLoader;

        }

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires pLoader to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('pLoader')) {

            this.pLoader = hlsConfig.pLoader;

        }

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires xhrSetup to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('xhrSetup')) {

            this.xhrSetup = hlsConfig.xhrSetup;

        }

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires fetchSetup to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('fetchSetup')) {

            this.fetchSetup = hlsConfig.fetchSetup;

        }

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires abrController to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('abrController')) {

            this.abrController = hlsConfig.abrController;

        }

        /**
         *
         * @type {function}
         *
         */
        // Something in hls.js requires timelineController to not exist at all, if it's not set
        if (hlsConfig && hlsConfig.hasOwnProperty('timelineController')) {

            this.timelineController = hlsConfig.timelineController;

        }

        /**
         *
         * @type {boolean}
         *
         */
        this.enableWebVTT = false;

        /**
         *
         * @type {boolean}
         *
         */
        this.enableCEA708Captions = true;

        /**
         *
         * @type {boolean}
         * @desc (default: `false`) If `true` will enable the default subtitle display. If using custom subtitle display, set to `false`
         *
         */
        this.subtitleDisplay = false;

        /**
         *
         * @type {string} Label for the text track generated for CEA-708 captions track 1. This is how it will appear in the browser's native menu for subtitles and captions.
         *
         */
        this.captionsTextTrack1Label = 'English';

        /**
         *
         * @type {boolean}
         *
         */
        this.stretchShortVideoTrack = false;

        /**
         *
         * @type {int}
         *
         */
        this.maxAudioFramesDrift  = 1;

        /**
         *
         * @type {boolean}
         *
         */
        this.forceKeyFrameOnDiscontinuity = true;

        /**
         *
         * @type {number}
         *
         */
        this.abrEwmaFastLive = 5.0;

        /**
         *
         * @type {number}
         *
         */
        this.abrEwmaSlowLive = 9.0;

        /**
         *
         * @type {number}
         *
         */
        this.abrEwmaFastVoD = 4.0;

        /**
         *
         * @type {number}
         *
         */
        this.abrEwmaSlowVoD = 15.0;

        /**
         *
         * @type {int}
         *
         */
        this.abrEwmaDefaultEstimate = 500000;

        /**
         *
         * @type {number}
         *
         */
        this.abrBandWidthFactor = 0.95;

        /**
         *
         * @type {number}
         *
         */
        this.abrBandWidthUpFactor = 0.7;

        /**
         *
         * @type {int}
         *
         */
        this.minAutoBitrate = 0;

        applyProperties(this, hlsConfig);

    }

}

export default HLSJSPlayerConfig;
